// App.tsx - Fixed
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiEvents } from './services/api';
import { useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import DataEntryForm from './pages/DataEntryForm';
import ManageRecords from './pages/ManageRecords';
import ManageStaffs from './pages/ManageStaffs';
import DailySummary  from './reports/DailySummary';
import RegisterReport from './reports/RegisterReport';
import AuditLog from './reports/AuditLog'
import ChangePassword from './pages/ChangePassword';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';

// Components
import PrivateRoute from './components/PrivateRoute';
import DashboardLayout from './components/DashboardLayout';

// Component to handle API events with access to router
function ApiEventHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Setup toast event listeners
    const handleSuccess = (message: string) => toast.success(message);
    const handleError = (message: string) => toast.error(message);
    const handleWarning = (message: string) => toast.warning(message);
    const handleInfo = (message: string) => toast.info(message);
    
    // Handle navigation events
    const handleNavigate = (path: string) => navigate(path);
    
    // Handle auth events
    const handleUnauthorized = () => {
      toast.error('Session expired. Please login again.');
      navigate('/login');
    };

    // Register all event listeners
    apiEvents.on('toast:success', handleSuccess);
    apiEvents.on('toast:error', handleError);
    apiEvents.on('toast:warning', handleWarning);
    apiEvents.on('toast:info', handleInfo);
    apiEvents.on('navigate', handleNavigate);
    apiEvents.on('auth:unauthorized', handleUnauthorized);

    // Cleanup on unmount
    return () => {
      apiEvents.off('toast:success', handleSuccess);
      apiEvents.off('toast:error', handleError);
      apiEvents.off('toast:warning', handleWarning);
      apiEvents.off('toast:info', handleInfo);
      apiEvents.off('navigate', handleNavigate);
      apiEvents.off('auth:unauthorized', handleUnauthorized);
    };
  }, [navigate]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const { user, isLoading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <ApiEventHandler />
      <ScrollToTop />
      <div className="min-h-screen bg-gray-50">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} /> 
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/*  
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          */}
          {/* Protected Routes - Wrapped with DashboardLayout */}
          <Route element={<PrivateRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/data-entry" element={<DataEntryForm />} />
              <Route path="/manage-records" element={<ManageRecords />} />
              <Route path="/manage-records/search" element={<ManageRecords />} />
              <Route path="/manage-records/my" element={<ManageRecords />} />
              <Route path="/manage-records/pending" element={<ManageRecords />} />
              <Route path="/manage-records/verified" element={<ManageRecords />} />
              <Route path="/manage-staffs" element={<ManageStaffs />} />
              <Route path="/reports/daily-summary" element={<DailySummary />} />
              <Route path="/reports/register-report" element={<RegisterReport />} />
              <Route path="/reports/audit-log" element={<AuditLog />} />
              <Route path="/change-password" element={<ChangePassword />} />
            </Route>
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {/* Toast Container */}
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false} 
        newestOnTop={false} 
        closeOnClick 
        rtl={false} 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
        theme="light"
      />
    </Router>
  );
}

export default App;